import type { AccountBilling, AccountBucket, FinanceReport, MissionCost, PnlProject, PrCost } from '@orgus/protocol';
import { COST_CATEGORIES, COST_CATEGORY_LABEL, UNKNOWN_ACCOUNT, formatUsd } from '@orgus/protocol';
import { BarChart3, CircleAlert, CircleHelp, Gauge, GitPullRequest, Info, Repeat, Target } from '@orgus/ui/icons';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { DataTable, EmptyState, Panel, RowStatus, Tooltip, formatPercent, plural } from '@orgus/ui';
import type { Column, RowStatusProps } from '@orgus/ui';
import { PnlChart } from './PnlChart.tsx';
import { dayText, formatHours, formatMinutes, usdOrNone } from './model.ts';
import { ReportGate, ReportSkeleton, ScopeBar, useScope } from './Scope.tsx';
import { StatStrip } from '../fleet/StatStrip.tsx';
import type { Stat } from '../fleet/StatStrip.tsx';
import { useFinanceReport } from './useFinance.ts';
import { AccountGlyph, billingLabel, kindLabel } from '../costs/accounts.tsx';

type RowState = Pick<RowStatusProps, 'tone' | 'icon' | 'label'>;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A PR's outcome, plain in its row (status.md): merged is grey, only a revert is loud. */
const PR_STATE: Record<PrCost['state'], RowState> = {
  merged: { tone: 'neutral', icon: 'done', label: 'Merged' },
  reverted: { tone: 'danger', icon: 'failed', label: 'Reverted' },
  abandoned: { tone: 'neutral', icon: 'ended', label: 'Closed unmerged' },
  open: { tone: 'neutral', icon: 'idle', label: 'Open' },
};

/** A tile's one short caption; the rest of the fact on hover or focus. */
function Caption({ text, more }: { text: string; more?: string }) {
  if (!more) return <>{text}</>;
  return (
    <Tooltip content={more}>
      <span tabIndex={0} className="rounded-control">
        {text}
      </span>
    </Tooltip>
  );
}

function KpiRow({ r }: { r: FinanceReport }) {
  const k = r.kpis;
  const other = r.total - k.tokens;
  const stats: Stat[] = [
    { label: 'Total cost', value: formatUsd(k.total), sub: <Caption text={`${formatUsd(k.tokens)} tokens`} more={`${formatUsd(other)} besides tokens`} /> },
    {
      label: 'Cost per merged PR',
      value: usdOrNone(k.costPerMergedPr),
      sub: k.mergedPrs ? <Caption text={plural(k.mergedPrs, 'merge')} more={`${usdOrNone(k.tokenCostPerMergedPr)} of each in tokens`} /> : 'No merges yet',
    },
    {
      label: 'Cost per active hour',
      value: usdOrNone(k.costPerActiveHour),
      unit: k.costPerActiveHour !== null ? 'tokens' : undefined,
      sub: <Caption text={`${formatHours(k.activeHours)} working`} more={`Of ${formatHours(k.sampledHours)} watched`} />,
    },
    { label: 'Cache hit ratio', value: k.cacheHitRatio === null ? 'None' : formatPercent(k.cacheHitRatio), sub: `Saved ${formatUsd(k.cacheSavings)}` },
    {
      label: 'Rework rate',
      // The value stays in fg; a high rate says so in a short coloured word in the caption.
      value: k.reworkRate === null ? 'None' : formatPercent(k.reworkRate),
      sub:
        k.attributedPrCost > 0 ? (
          <>
            {k.reworkRate !== null && k.reworkRate > 0.2 && <span className="font-medium text-status-attention">High, </span>}
            <Caption text={`${formatUsd(k.reworkCost)} reworked`} more="Spent on reverted or abandoned PRs" />
          </>
        ) : (
          <Caption text="No PR sessions" more="Needs PRs with sessions on their branch" />
        ),
    },
    {
      label: 'Approval wait',
      value: formatMinutes(k.approvalWait.medianMinutes),
      unit: k.approvalWait.medianMinutes !== null ? 'median' : undefined,
      sub: k.approvalWait.episodes ? (
        <Caption text={plural(k.approvalWait.episodes, 'wait')} more={`${formatHours(k.approvalWait.hours)} in all, 90% under ${formatMinutes(k.approvalWait.p90Minutes)}`} />
      ) : (
        'No waits'
      ),
    },
  ];
  return <StatStrip label="Cost and delivery" stats={stats} />;
}

/** A mission's state, plain in its row: done is grey; running, needs you and failed carry colour. */
const MISSION_STATE: Record<string, RowState> = {
  planned: { tone: 'neutral', icon: 'queued', label: 'Planned' },
  running: { tone: 'working', icon: 'working', label: 'Running' },
  waiting: { tone: 'attention', icon: 'needs-you', label: 'Needs you' },
  done: { tone: 'neutral', icon: 'done', label: 'Done' },
  failed: { tone: 'danger', icon: 'failed', label: 'Failed' },
  stopped: { tone: 'neutral', icon: 'stopped', label: 'Stopped' },
};
const UNKNOWN_STATE: RowState = { tone: 'neutral', icon: 'idle', label: 'Unknown' };

const projectColumns: Column<PnlProject>[] = [
  {
    key: 'project',
    header: 'Project',
    grow: true,
    text: (p) => p.label,
    cell: (p) =>
      p.projectId ? (
        <Link to={`/spend/pnl?project=${encodeURIComponent(p.projectId)}`} className="text-fg underline-offset-2 hover:underline">
          {p.label}
        </Link>
      ) : (
        <span className="flex items-center gap-1.5 text-fg-muted">
          <CircleAlert className="size-3.5 text-waiting" aria-hidden />
          {p.label}
        </span>
      ),
  },
  ...COST_CATEGORIES.map(
    (c): Column<PnlProject> => ({
      key: c,
      header: COST_CATEGORY_LABEL[c].replace(' and subscriptions', ''),
      type: 'number',
      hideBelow: c === 'tokens' ? undefined : 'lg',
      sortValue: (p) => p.byCategory[c],
      cell: (p) => (p.byCategory[c] ? formatUsd(p.byCategory[c]) : <span className="text-fg-subtle">$0</span>),
    }),
  ),
  { key: 'total', header: 'Total', type: 'number', sortValue: (p) => p.total, cell: (p) => <span className="font-medium text-fg">{formatUsd(p.total)}</span> },
];

const BILLING_ICON: Record<AccountBilling, ReactNode> = { subscription: <Repeat />, usage: <Gauge />, unknown: <CircleHelp /> };

const accountColumns: Column<AccountBucket>[] = [
  {
    key: 'account',
    header: 'Account',
    grow: true,
    text: (a) => `${a.label} ${kindLabel(a.kind)}`,
    cell: (a) => (
      <span className="flex min-w-0 items-center gap-2">
        <AccountGlyph kind={a.kind} />
        <Link to={`/spend/pnl?account=${encodeURIComponent(a.key || UNKNOWN_ACCOUNT)}`} className="truncate text-fg underline-offset-2 hover:underline">
          {a.label}
        </Link>
        {a.kind && <span className="hidden shrink-0 text-caption text-fg-subtle md:inline">{kindLabel(a.kind)}</span>}
      </span>
    ),
  },
  {
    key: 'billing',
    header: 'Billing',
    hideBelow: 'md',
    text: (a) => billingLabel(a.billing),
    cell: (a) => (
      <span className="flex items-center gap-2 text-fg-muted [&_svg]:size-3.5 [&_svg]:text-fg-subtle">
        {BILLING_ICON[a.billing]}
        {billingLabel(a.billing)}
      </span>
    ),
  },
  { key: 'sessions', header: 'Sessions', type: 'number', hideBelow: 'md', sortValue: (a) => a.sessions, cell: (a) => a.sessions },
  { key: 'cost', header: 'Token cost', type: 'number', sortValue: (a) => a.cost, cell: (a) => <span className="font-medium text-fg">{formatUsd(a.cost)}</span> },
];

const prColumns: Column<PrCost>[] = [
  {
    key: 'pr',
    header: 'Pull request',
    grow: true,
    text: (p) => `${p.repo} #${p.number} ${p.title}`,
    cell: (p) => (
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-fg">{p.title}</span>
        {/* On a phone the outcome rides under the title, so the title keeps the row. */}
        <RowStatus {...PR_STATE[p.state]} className="text-caption md:hidden" />
        <span className="hidden truncate font-mono text-caption text-fg-subtle md:block">
          {p.repo}#{p.number}, {p.branch}
        </span>
      </span>
    ),
  },
  {
    key: 'state',
    header: 'Outcome',
    hideBelow: 'md',
    text: (p) => PR_STATE[p.state].label,
    cell: (p) => <RowStatus {...PR_STATE[p.state]} />,
  },
  { key: 'sessions', header: 'Sessions', type: 'number', hideBelow: 'md', sortValue: (p) => p.sessions, cell: (p) => p.sessions },
  { key: 'cost', header: 'Cost', type: 'number', sortValue: (p) => p.cost, cell: (p) => (p.sessions ? formatUsd(p.cost) : <span className="text-fg-subtle">No sessions seen</span>) },
];

const missionColumns: Column<MissionCost>[] = [
  {
    key: 'title',
    header: 'Mission',
    grow: true,
    text: (m) => m.title,
    cell: (m) => (
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-fg">{m.title}</span>
        {/* On a phone the state rides under the title, as in the pull requests table. */}
        <RowStatus {...(MISSION_STATE[m.state ?? ''] ?? UNKNOWN_STATE)} className="text-caption md:hidden" />
      </span>
    ),
  },
  {
    key: 'state',
    header: 'State',
    hideBelow: 'md',
    text: (m) => m.state ?? '',
    cell: (m) => <RowStatus {...(MISSION_STATE[m.state ?? ''] ?? UNKNOWN_STATE)} />,
  },
  { key: 'sessions', header: 'Sessions', type: 'number', hideBelow: 'md', sortValue: (m) => m.sessions, cell: (m) => m.sessions },
  { key: 'cost', header: 'Cost', type: 'number', sortValue: (m) => m.cost, cell: (m) => formatUsd(m.cost) },
];

/** The project P&L: what building costs, by week and category, and what it bought. */
export function PnlView() {
  const scope = useScope();
  const result = useFinanceReport(scope);
  return (
    <>
      <ScopeBar scope={scope} result={result} />
      <ReportGate result={result} skeleton={<ReportSkeleton />}>
        {(r) => (
          <div className="flex flex-col gap-5">
            {r.unallocated.length > 0 && (
              <section aria-label="Unattributed cost" className="flex flex-col divide-y divide-border-subtle overflow-hidden rounded-card border border-border bg-surface">
                {r.unallocated.map((u) => (
                  <div key={u.category} className="flex min-h-row items-center gap-2 px-row-x py-2 text-ui">
                    <CircleAlert className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                    <span className="min-w-0 flex-1 text-fg">
                      {formatUsd(u.amount)} of {u.category === 'tokens' ? 'token cost' : COST_CATEGORY_LABEL[u.category].toLowerCase()} has no project
                      {u.why && (
                        <Tooltip content={u.why}>
                          <span tabIndex={0} aria-label="Why it has no project" className="ml-2 inline-flex rounded-control align-middle text-fg-subtle hover:text-fg-muted">
                            <CircleHelp className="size-4" aria-hidden />
                          </span>
                        </Tooltip>
                      )}
                    </span>
                    {u.category === 'tokens' && (
                      <Link to="/sessions?project=none" className="shrink-0 font-medium text-fg-muted underline-offset-2 hover:text-fg hover:underline">
                        Show sessions
                      </Link>
                    )}
                  </div>
                ))}
              </section>
            )}
            <KpiRow r={r} />
            {r.total === 0 ? (
              <Panel title="Cost by week" icon={<BarChart3 />}>
                <EmptyState
                  icon={<BarChart3 />}
                  title="Nothing spent in this period"
                  description="Add seats, invoices and other costs in Ledger."
                  action={
                    <Link to="/spend/ledger" className="text-sm font-medium text-fg underline-offset-2 hover:underline">
                      Open Ledger
                    </Link>
                  }
                />
              </Panel>
            ) : (
              <PnlChart weeks={r.weeks} />
            )}
            <section aria-label="By project" className="flex flex-col gap-2">
              <h2 className="flex items-center gap-2 text-base font-medium text-fg">
                By project and category
                <span className="hidden text-caption font-normal text-fg-subtle md:inline">Shared costs split by each project's token spend</span>
              </h2>
              <DataTable
                label="Cost by project and category"
                rows={r.projects}
                columns={projectColumns}
                rowKey={(p) => p.projectId || 'none'}
                pageSize={false}
                initialSort={{ key: 'total', dir: 'desc' }}
                empty={<EmptyState title="No project costs in this period" description="Costs show here per project once sessions run or entries are added." />}
              />
            </section>
            {scope.account === null && r.accounts && r.accounts.length > 0 && (
              <section aria-label="By account" className="flex flex-col gap-2">
                <h2 className="flex items-center gap-2 text-base font-medium text-fg">
                  By account
                  <span className="text-caption font-normal text-fg-subtle">Token spend</span>
                </h2>
                <DataTable
                  label="Token spend by account"
                  rows={r.accounts}
                  columns={accountColumns}
                  rowKey={(a) => a.key || 'unknown'}
                  pageSize={false}
                  initialSort={{ key: 'cost', dir: 'desc' }}
                />
              </section>
            )}
            <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <section aria-label="Pull requests" className="flex flex-col gap-2">
                <h2 className="text-base font-medium text-fg">Pull requests closed in this period</h2>
                <DataTable
                  label="Pull requests and their cost"
                  rows={r.prs}
                  columns={prColumns}
                  rowKey={(p) => `${p.repo}#${p.number}`}
                  pageSize={10}
                  initialSort={{ key: 'cost', dir: 'desc' }}
                  empty={
                    <EmptyState
                      icon={<GitPullRequest />}
                      title="No pull requests yet"
                      description="Add a GitHub token to cost each closed pull request by its sessions."
                      action={
                        <Link to="/settings/finance" className="text-sm font-medium text-fg underline-offset-2 hover:underline">
                          Open finance settings
                        </Link>
                      }
                    />
                  }
                />
              </section>
              <section aria-label="Missions" className="flex flex-col gap-2">
                <h2 className="text-base font-medium text-fg">
                  Missions <span className="text-caption font-normal text-fg-subtle">{r.kpis.costPerMission !== null ? `${formatUsd(r.kpis.costPerMission)} each on average` : ''}</span>
                </h2>
                <DataTable
                  label="Cost per mission"
                  rows={r.kpis.missions}
                  columns={missionColumns}
                  rowKey={(m) => m.missionId}
                  pageSize={8}
                  initialSort={{ key: 'cost', dir: 'desc' }}
                  empty={<EmptyState icon={<Target />} title="No mission spend" description="Sessions started from a mission are costed to it." />}
                />
              </section>
            </div>
            <p className="flex items-center gap-2 text-caption text-fg-subtle">
              <Info className="size-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 truncate">Tokens at the {ISO_DAY.test(r.pricingVersion) ? dayText(r.pricingVersion) : r.pricingVersion} API list price, USD</span>
              {r.billing === 'subscription' && (
                <Tooltip content="Subscription seats have no per-token bill, so their token amounts are notional">
                  <span tabIndex={0} className="shrink-0 rounded-control underline decoration-dotted underline-offset-2">
                    Notional
                  </span>
                </Tooltip>
              )}
            </p>
          </div>
        )}
      </ReportGate>
    </>
  );
}
